import { useState, useEffect } from 'react';
import axios from 'axios';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import Autocomplete from '@mui/material/Autocomplete';
import ImageIcon from '@mui/icons-material/Image';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { useSnackbar } from 'notistack';
import { useNavigate, useParams } from 'react-router-dom';
import MetaData from '../Layouts/MetaData';
import BackdropLoader from '../Layouts/BackdropLoader';

const TrustPromoForm = () => {
    const { id } = useParams();
    const isEditMode = Boolean(id);

    const { enqueueSnackbar } = useSnackbar();
    const navigate = useNavigate();

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    const [categories, setCategories] = useState([]);
    const [productOptions, setProductOptions] = useState([]);
    const [brandOptions, setBrandOptions] = useState([]);

    const [adminLabel, setAdminLabel] = useState("");
    const [label, setLabel] = useState("This Week");
    const [labelZh, setLabelZh] = useState("");
    const [heading, setHeading] = useState("");
    const [headingZh, setHeadingZh] = useState("");
    const [description, setDescription] = useState("");
    const [descriptionZh, setDescriptionZh] = useState("");
    const [order, setOrder] = useState(0);
    const [active, setActive] = useState(true);
    const [showOverlay, setShowOverlay] = useState(true);
    const [showButton, setShowButton] = useState(true);
    const [buttonText, setButtonText] = useState("Shop Now");
    const [buttonTextZh, setButtonTextZh] = useState("");

    const [linkType, setLinkType] = useState("category");
    const [linkProduct, setLinkProduct] = useState(null); // { _id, name } | null
    const [categorySlug, setCategorySlug] = useState("");
    const [linkBrand, setLinkBrand] = useState("");

    const [image, setImage] = useState("");
    const [imagePreview, setImagePreview] = useState("");
    const [imageZh, setImageZh] = useState("");
    const [imageZhPreview, setImageZhPreview] = useState("");

    useEffect(() => {
        const fetchOptions = async () => {
            try {
                const [categoriesRes, productsRes, brandsRes] = await Promise.all([
                    axios.get('/api/v1/categories'),
                    axios.get('/api/v1/admin/products'),
                    axios.get('/api/v1/admin/brands'),
                ]);
                setCategories(categoriesRes.data.categories);
                setProductOptions(productsRes.data.products.map((p) => ({ _id: p._id, name: p.name })));
                setBrandOptions(brandsRes.data.brands.map((b) => b.name));
            } catch (error) {
                console.error('Failed to load link picker options', error);
            }
        };
        fetchOptions();
    }, []);

    // Same approach as the other CMS forms: no single-item GET endpoint,
    // so in edit mode we fetch the admin list and find this one by id.
    useEffect(() => {
        if (!isEditMode) {
            setLoading(false);
            return;
        }
        const fetchPromo = async () => {
            try {
                const { data } = await axios.get('/api/v1/admin/trust-promos');
                const promo = data.promos.find((p) => p._id === id);
                if (!promo) {
                    enqueueSnackbar("Trust promo not found", { variant: "error" });
                    navigate('/admin/trust-promos');
                    return;
                }
                setAdminLabel(promo.adminLabel || "");
                setLabel(promo.label || "This Week");
                setLabelZh(promo.labelZh || "");
                setHeading(promo.heading || "");
                setHeadingZh(promo.headingZh || "");
                setDescription(promo.description || "");
                setDescriptionZh(promo.descriptionZh || "");
                setOrder(promo.order || 0);
                setActive(promo.active);
                setShowOverlay(promo.showOverlay !== false);
                setShowButton(promo.showButton !== false);
                setButtonText(promo.buttonText || "Shop Now");
                setButtonTextZh(promo.buttonTextZh || "");
                setLinkType(promo.linkType || "category");
                setLinkProduct(promo.linkProduct ? { _id: promo.linkProduct._id, name: promo.linkProduct.name } : null);
                setCategorySlug(promo.categorySlug || "");
                setLinkBrand(promo.linkBrand || "");
                setImagePreview(promo.image?.url || "");
                setImageZhPreview(promo.imageZh?.url || "");
            } catch (error) {
                enqueueSnackbar(error?.response?.data?.message || "Failed to load promo", { variant: "error" });
            } finally {
                setLoading(false);
            }
        };
        fetchPromo();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = () => {
            if (reader.readyState === 2) {
                setImagePreview(reader.result);
                setImage(reader.result);
            }
        };
        reader.readAsDataURL(file);
    };

    const handleImageZhChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = () => {
            if (reader.readyState === 2) {
                setImageZhPreview(reader.result);
                setImageZh(reader.result);
            }
        };
        reader.readAsDataURL(file);
    };

    const submitHandler = async (e) => {
        e.preventDefault();

        if (!adminLabel.trim()) {
            enqueueSnackbar("Please enter an admin label (for identifying this card in the list)", { variant: "warning" });
            return;
        }
        if (linkType === 'product' && !linkProduct) {
            enqueueSnackbar("Please choose a product to link to", { variant: "warning" });
            return;
        }
        if (linkType === 'category' && !categorySlug) {
            enqueueSnackbar("Please choose a category to link to", { variant: "warning" });
            return;
        }
        if (linkType === 'brand' && !linkBrand) {
            enqueueSnackbar("Please choose a brand to link to", { variant: "warning" });
            return;
        }

        const payload = {
            adminLabel, label, labelZh, heading, headingZh, description, descriptionZh, order, active,
            showOverlay, showButton, buttonText, buttonTextZh,
            linkType,
            linkProduct: linkProduct?._id,
            categorySlug,
            linkBrand,
        };
        if (image) payload.image = image;
        if (imageZh) payload.imageZh = imageZh;

        setSaving(true);
        try {
            if (isEditMode) {
                await axios.put(`/api/v1/admin/trust-promo/${id}`, payload);
                enqueueSnackbar("Trust promo updated", { variant: "success" });
            } else {
                await axios.post('/api/v1/admin/trust-promo/new', payload);
                enqueueSnackbar("Trust promo created", { variant: "success" });
            }
            navigate('/admin/trust-promos');
        } catch (error) {
            enqueueSnackbar(error?.response?.data?.message || "Failed to save promo", { variant: "error" });
        } finally {
            setSaving(false);
        }
    };

    if (loading) return <BackdropLoader />;

    return (
        <>
            <MetaData title={`Admin: ${isEditMode ? "Edit" : "New"} Trust Promo | 1313 Mart`} />
            {saving && <BackdropLoader />}

            <form onSubmit={submitHandler} className="flex flex-col gap-4 bg-white rounded-lg shadow p-4 sm:p-6" id="trustpromoform">

                <h1 className="text-lg font-medium uppercase">{isEditMode ? "Edit" : "New"} Trust Promo</h1>

                <TextField
                    label="Admin Label"
                    variant="outlined"
                    size="small"
                    required
                    fullWidth
                    value={adminLabel}
                    onChange={(e) => setAdminLabel(e.target.value)}
                    helperText="For identifying this card in the admin list only — never shown on the website"
                />

                <div className="flex flex-col sm:flex-row gap-4">
                    <TextField
                        label="Promo Label (e.g. 'This Week')"
                        variant="outlined"
                        size="small"
                        fullWidth
                        value={label}
                        onChange={(e) => setLabel(e.target.value)}
                    />
                    <TextField
                        label="Promo Label (中文 — optional)"
                        variant="outlined"
                        size="small"
                        fullWidth
                        value={labelZh}
                        onChange={(e) => setLabelZh(e.target.value)}
                    />
                </div>

                <div className="flex flex-col sm:flex-row gap-4">
                    <TextField
                        label="Heading (optional)"
                        variant="outlined"
                        size="small"
                        fullWidth
                        value={heading}
                        onChange={(e) => setHeading(e.target.value)}
                        helperText="Shown on the card — independent of whatever it links to below"
                    />
                    <TextField
                        label="Heading (中文 — optional)"
                        variant="outlined"
                        size="small"
                        fullWidth
                        value={headingZh}
                        onChange={(e) => setHeadingZh(e.target.value)}
                    />
                </div>

                <div>
                    <p className="text-sm text-gray-600 mb-1">Description (optional)</p>
                    <ReactQuill theme="snow" value={description} onChange={setDescription} />
                </div>

                <div>
                    <p className="text-sm text-gray-600 mb-1">Description (中文 — optional)</p>
                    <ReactQuill theme="snow" value={descriptionZh} onChange={setDescriptionZh} />
                </div>

                <TextField
                    label="Display Order"
                    type="number"
                    variant="outlined"
                    size="small"
                    value={order}
                    onChange={(e) => setOrder(e.target.value)}
                    helperText="Lower numbers show first"
                    sx={{ maxWidth: 200 }}
                />

                <FormControlLabel
                    control={<Checkbox checked={active} onChange={(e) => setActive(e.target.checked)} />}
                    label="Active (visible on homepage)"
                />

                <FormControlLabel
                    control={<Checkbox checked={showOverlay} onChange={(e) => setShowOverlay(e.target.checked)} />}
                    label="Dark overlay (improves text readability over busy photos)"
                />

                <FormControlLabel
                    control={<Checkbox checked={showButton} onChange={(e) => setShowButton(e.target.checked)} />}
                    label="Show button"
                />

                {showButton && (
                    <div className="flex gap-4">
                        <TextField
                            label="Button Text"
                            variant="outlined"
                            size="small"
                            value={buttonText}
                            onChange={(e) => setButtonText(e.target.value)}
                            sx={{ maxWidth: 260 }}
                        />
                        <TextField
                            label="Button Text (中文)"
                            variant="outlined"
                            size="small"
                            value={buttonTextZh}
                            onChange={(e) => setButtonTextZh(e.target.value)}
                            sx={{ maxWidth: 260 }}
                        />
                    </div>
                )}

                {/* --- Link settings --- */}
                <div className="border rounded-lg p-4 flex flex-col gap-4 bg-gray-50">
                    <p className="text-sm font-medium text-gray-700">
                        Card Link — the category/product/brand name is never shown on the card itself, only used to decide where the button goes.
                    </p>

                    <TextField
                        label="Link Type"
                        select
                        variant="outlined"
                        size="small"
                        fullWidth
                        value={linkType}
                        onChange={(e) => setLinkType(e.target.value)}
                    >
                        <MenuItem value="none">No link</MenuItem>
                        <MenuItem value="product">A specific product</MenuItem>
                        <MenuItem value="category">A category (product listing)</MenuItem>
                        <MenuItem value="brand">A brand</MenuItem>
                    </TextField>

                    {linkType === 'product' && (
                        <Autocomplete
                            options={productOptions}
                            getOptionLabel={(opt) => opt.name || ''}
                            isOptionEqualToValue={(opt, val) => opt._id === val._id}
                            value={linkProduct}
                            onChange={(e, newValue) => setLinkProduct(newValue)}
                            renderInput={(params) => (
                                <TextField {...params} label="Choose a product" size="small" placeholder="Search products..." />
                            )}
                        />
                    )}

                    {linkType === 'category' && (
                        <TextField
                            label="Choose a category"
                            select
                            variant="outlined"
                            size="small"
                            fullWidth
                            value={categorySlug}
                            onChange={(e) => setCategorySlug(e.target.value)}
                        >
                            {categories.map((cat) => (
                                <MenuItem value={cat.slug} key={cat._id}>{cat.name}</MenuItem>
                            ))}
                        </TextField>
                    )}

                    {linkType === 'brand' && (
                        <Autocomplete
                            options={brandOptions}
                            value={linkBrand || null}
                            onChange={(e, newValue) => setLinkBrand(newValue || "")}
                            renderInput={(params) => (
                                <TextField {...params} label="Choose a brand" size="small" placeholder="Search brands..." />
                            )}
                        />
                    )}
                </div>
                {/* --- Link settings --- */}

                <div>
                    <p className="text-sm text-gray-600 mb-1">Background Image (optional)</p>
                    <div className="flex items-center gap-4">
                        <div className="w-40 h-24 flex items-center justify-center border rounded-lg overflow-hidden bg-gray-50">
                            {!imagePreview ? <ImageIcon className="text-gray-400" /> :
                                <img draggable="false" src={imagePreview} alt="Promo" className="w-full h-full object-cover" />
                            }
                        </div>
                        <label className="rounded bg-gray-400 text-center cursor-pointer text-white py-2 px-3 shadow hover:shadow-lg">
                            <input
                                type="file"
                                accept="image/*"
                                onChange={handleImageChange}
                                className="hidden"
                            />
                            Choose Image
                        </label>
                    </div>
                </div>

                <div>
                    <p className="text-sm text-gray-600 mb-1">Background Image (中文 — optional)</p>
                    <p className="text-xs text-gray-400 mb-2">Shown instead of the image above when a visitor switches to Chinese. Leave blank to reuse the image above.</p>
                    <div className="flex items-center gap-4">
                        <div className="w-40 h-24 flex items-center justify-center border rounded-lg overflow-hidden bg-gray-50">
                            {!imageZhPreview ? <ImageIcon className="text-gray-400" /> :
                                <img draggable="false" src={imageZhPreview} alt="Promo (Chinese)" className="w-full h-full object-cover" />
                            }
                        </div>
                        <label className="rounded bg-gray-400 text-center cursor-pointer text-white py-2 px-3 shadow hover:shadow-lg">
                            <input
                                type="file"
                                accept="image/*"
                                onChange={handleImageZhChange}
                                className="hidden"
                            />
                            Choose Image
                        </label>
                    </div>
                </div>

                <div className="flex justify-end">
                    <input form="trustpromoform" type="submit" className="bg-primary-orange uppercase px-6 py-3 text-white font-medium rounded shadow hover:shadow-lg cursor-pointer" value={isEditMode ? "Save Changes" : "Create Promo"} />
                </div>

            </form>
        </>
    );
};

export default TrustPromoForm;